import { quadCentre, quadDrift } from "@webscan/stability.js";
import type { PoseDetection } from "./pose";

/**
 * The handheld gate: when a card counts as placed, and when a placement has been dealt
 * with.
 *
 * Pure policy over the detector's quads — no frames, no pixels, no clock of its own
 * beyond `performance.now()`. The scan loop feeds it one leading detection per frame
 * and asks whether to spend a read; everything about *why* a read is or is not spent
 * lives here, with the constants that decide it.
 */

/** Camera frames the gate must see a still card in before recognition is paid for. */
export const HOLD_LABEL = 3;

/**
 * When a card counts as held still, and when it counts as exchanged.
 *
 * All of these are *rates and durations*, not per-frame pixel budgets. The first cut
 * used per-frame drift thresholds tuned at the iPhone's ~10 fps; a desktop at 55 fps
 * divides the same hand motion across five times the frames, so slowly laying a card
 * down measured as "still", the gate fired mid-placement, the junk read latched the
 * placement as done — and the card resting a moment later was never read at all.
 *
 * Motion is measured against the *card* rather than the frame: shake is proportional
 * to how far away the phone is, and so is the card's size on screen. The drift metric
 * itself is still the ported `quadDrift`, cyclic-shift-minimised; only the units are
 * local policy.
 */
//: Below this the card is resting — card-sizes per second. Roomy on purpose: the
//: detector's corner jitter scales with how small the card sits in the frame, and a
//: threshold the jitter can graze turns every read into the motion timeout. Genuine
//: The native handheld sample puts resting p75 at 0.53/s and motion above 2/s.
//: 0.8 gives resting jitter room without changing the exchange threshold.
const STILL_VELOCITY = 0.8;
//: Past this the card is being moved or exchanged, not wobbling: re-arm the gate.
const REARM_VELOCITY = 1.2;
//: Velocity is measured across this window, never frame-to-frame: at 55 fps the
//: detector's own corner jitter divided by an 18 ms dt reads as ~0.37 sizes/s — over
//: the still threshold — and a motionless card never counted as still. Across 100 ms
//: the same jitter amortises to well under the threshold and real motion still
//: classifies within one window.
const VELOCITY_WINDOW_MS = 100;
//: Continuous stillness before a read — with the window above, rest-to-fire lands at
//: roughly 150-350 ms. Much lower would mean reading hands: ~1.5 still windows is the
//: minimum that says "placed", not "passing through".
const STILL_MS = 150;
//: Escape hatch for a card that never stops moving — a hand, or a phone on the move.
const MOTION_TIMEOUT_MS = 1200;
/** Past this the card has been put somewhere else, and the track starts over. */
const REPLACED_FRACTION = 0.5;
/**
 * Floor between two reads, across placements and dropouts.
 *
 * The gate already fires once per resting placement; this additionally stops a
 * flickering detection — reset, re-track, re-fire, every few frames — from turning
 * into a read storm. Reads run detached from the frame loop, so this is a spam guard,
 * not a viewfinder-latency lever.
 */
const READ_COOLDOWN_MS = 350;
/**
 * When a rejected placement is read again, measured from the read that was rejected.
 *
 * Sleeve gloss was the motivating case — the sheen moves with every hand tremor, so the
 * next frame of the same placement is genuinely new evidence — and at the read cooldown's
 * pace the whole budget was spent inside two seconds of the card being laid down. Those
 * are the worst two seconds there are: the hand is still leaving the frame, the card is
 * still rocking, and a phone camera is still hunting focus on a subject that moved. Miss
 * all four and the placement was latched for good, so the card lay there — sharp, still,
 * perfectly readable — and the scanner had already finished deciding about it.
 *
 * Covering the lens and uncovering it read that card immediately, which is the tell: the
 * card was never the problem, the attempts were simply all spent before it was worth
 * reading one. A gap that long destroys the placement, and a placement that starts over
 * gets a fresh budget — at the one moment when everything has settled.
 *
 * So the attempts spread out rather than cluster, the last landing some twelve seconds
 * in. Still bounded, because a card the index does not know must not be re-read for the
 * rest of the session — and by then "no match" has been on screen long enough to act on.
 */
export const RETAKE_DELAYS = [350, 800, 1800, 4000];
export const MAX_RETAKES = RETAKE_DELAYS.length;
/**
 * When a warped crop is too soft to be worth reading, and how long a placement may hold
 * out for a sharper one.
 *
 * The bar is *relative*, and deliberately: what counts as sharp depends on the lens, the
 * light, the distance and the card, and an absolute threshold picked here would be wrong
 * on the first device that disagreed with it — silently, by refusing to read anything. So
 * the bar tracks the best crop this session has actually produced, with a slow decay so a
 * single freak frame cannot raise it for good, and a fraction of that is the floor. A card
 * measurably softer than what this phone was managing a moment ago is a card mid-refocus.
 * A third of the bar is roughly a two-pixel blur on the canonical raster: mild softness
 * still reads, because a soft crop that identifies is worth more than a sharp one waited
 * for, and only a crop that is clearly out of focus is worth holding an attempt back for.
 *
 * Bounded on both sides. Waiting is capped, so a genuinely soft card — a flat full-art, a
 * dim table — is read rather than held out for a sharpness it will never reach; and the
 * wait between looks is short, because nothing was spent, so there is nothing to space
 * out. Worst case the fix costs a placement one second and then behaves exactly as before.
 */
export const FOCUS_RATIO = 0.35;
export const FOCUS_DECAY = 0.97;
export const FOCUS_RETRY_MS = 150;
export const FOCUS_WAIT_MS = 1200;
/**
 * A detection gap shorter than this, with the card back where it was, is the same card.
 *
 * A grainy frame — low light, sensor noise, a hand's shadow — drops the detector's
 * confidence under its floor for a frame or two at a time. Without this the track
 * restarted on every such frame, and with it `fired` and the retake count: a motionless
 * card was re-identified every ~600 ms forever, and a card the index cannot read got
 * three fresh retakes after each blink. The read is expensive and the answer cannot
 * change, so the placement has to outlive the dropout.
 *
 * What it must not do is heal an *exchange*. Lifting a card away in full view drives the
 * velocity over the re-arm threshold before the detection is lost, but a swap done under
 * the user's hand is invisible from end to end: the hand hides card A, and the detection
 * that comes back is card B, in the same spot, inside this window. Geometry cannot tell
 * those two apart at all — which is why anything longer than a blink has to be proven by
 * content before the latch is honoured (`needsProof`).
 */
const DROPOUT_GRACE_MS = 700;
/**
 * How long a gap can be and still be a *detector* blink rather than a hole in the record.
 *
 * A confidence dropout is one to three frames — 25-75 ms at the detector's cap — and
 * healing one is free. Longer than this and the gate has been blind for long enough that
 * a hand could have been over the card, so a resumed latch is provisional: same place is
 * no longer evidence of same card.
 */
const BLINK_MS = 120;

/**
 * Fires once per placement, when the card settles — or when it clearly never will.
 *
 * A local policy rather than `StabilityGate` because the thresholds are the whole point
 * and that class reads them from shared constants tuned for a webcam on a desk.
 */
export class HandheldGate {
  /** An asynchronous read may refine only the placement it actually sampled. */
  placementId = 0;
  private nextPlacementId = 0;
  /** The quad at the start of the current velocity window. */
  private baseline: Float32Array | null = null;
  private baselineAt = 0;
  private centre: [number, number] | null = null;
  /** Where the card was when it was read; walking away from there re-arms the gate. */
  private anchor: [number, number] | null = null;
  private stillSince: number | null = null;
  private trackedSince: number | null = null;
  held = 0;
  tracked = 0;
  /** Velocity of the last completed window, in card-sizes/s — for the frame trace. */
  lastVelocity: number | null = null;
  /** Why the last fire happened — "still 312ms" or "motion timeout" — for the log. */
  fireReason = "";
  private fired = false;
  /**
   * Whether the read was taken on a *still* card. A read forced by the motion timeout
   * gets one retake once the card settles — otherwise a junk mid-placement read
   * latches the placement and the resting card is never read at all.
   */
  private firedStill = false;
  // Deliberately not cleared by reset(): a one-frame detection dropout resets the
  // track, and an instant re-fire on re-acquisition would read the same card again.
  // It is a rate limit, not a latch — the latch is `placement` below.
  private lastFired = 0;
  /** Size of the last tracked quad, kept so a dropout can be measured in card widths. */
  private size = 0;
  /**
   * The placement the track was on when detection was lost, and when that was.
   *
   * Survives `reset()` by construction — it is written by the loss and read by the
   * re-acquisition — and is what makes a blink in the detection cost nothing.
   */
  private placement: {
    id: number;
    at: number;
    centre: [number, number];
    size: number;
    fired: boolean;
    firedStill: boolean;
    retakes: number;
  } | null = null;
  /** Set when a dropout was healed rather than restarted; read once by the loop's log. */
  private resumed = false;
  /** When the last sample arrived — what a gap in the evidence is measured from. */
  private lastSeenAt = 0;
  /**
   * Set when a latch was carried across a gap wide enough for the card to have been
   * exchanged inside it. The loop must then prove the card in frame is the one that was
   * read — geometry has already said as much as it can, and "same place" is not "same
   * card" — before the latch is allowed to suppress a read.
   */
  needsProof = false;

  private reset(): void {
    this.placementId = ++this.nextPlacementId;
    this.baseline = null;
    this.centre = null;
    this.anchor = null;
    this.stillSince = null;
    this.trackedSince = null;
    this.held = 0;
    this.tracked = 0;
    this.fired = false;
    this.firedStill = false;
    this.needsProof = false;
    this.retakes = 0;
    this.gaveUp = false;
    this.deferred = 0;
    this.deferUntil = 0;
  }

  /** Rejected reads granted for the current placement so far. */
  private retakes = 0;
  /** Set when that budget ran out with the card still unidentified. */
  private gaveUp = false;
  /** How long this placement has waited for a sharper crop, and until when it is waiting. */
  private deferred = 0;
  private deferUntil = 0;

  /**
   * Detection lost. Remember what the track had earned before clearing it.
   *
   * Only a track that has actually fired (or spent retakes) is worth remembering: that
   * is the state whose loss costs a duplicate read. Stamped with the last sample the
   * track actually had rather than with now, so the grace window measures the gap and
   * not the wait — and so a hole noticed one frame late is still measured from its start.
   */
  private drop(): void {
    if (this.centre && (this.fired || this.retakes > 0)) {
      this.placement = {
        id: this.placementId,
        at: this.lastSeenAt,
        centre: this.centre,
        size: this.size,
        fired: this.fired,
        firedStill: this.firedStill,
        retakes: this.retakes,
      };
    }
    this.reset();
  }

  /**
   * Pick a dropped track back up when the card never went anywhere.
   *
   * Stillness is deliberately *not* restored — the velocity window has a hole in it and
   * the card has to earn "resting" again. Only the read latch and the retake budget come
   * back, which is exactly what stops the re-read. The anchor comes with them, so a card
   * that is genuinely carried off after the gap still re-arms the normal way.
   */
  private resume(centre: [number, number], size: number, now: number): void {
    const placement = this.placement;
    this.placement = null;
    if (!placement || now - placement.at > DROPOUT_GRACE_MS) return;
    const moved = Math.hypot(centre[0] - placement.centre[0], centre[1] - placement.centre[1]);
    if (moved > REPLACED_FRACTION * Math.max(size, placement.size)) return;
    this.placementId = placement.id;
    this.fired = placement.fired;
    this.firedStill = placement.firedStill;
    this.retakes = placement.retakes;
    this.anchor = placement.centre;
    this.resumed = true;
    // Past a blink the latch is restored on trust, not on evidence: a hand can cover a
    // card, take it and lay the next one down without the detector seeing any of it, and
    // the far side of that gap looks exactly like the far side of a dropout. So the latch
    // is provisional until the loop has compared what the card looks like now with what
    // the card it read looked like.
    if (this.fired && now - placement.at > BLINK_MS) this.needsProof = true;
  }

  /** Whether the last update healed a dropout — true once per gap, for the log. */
  takeResumed(): boolean {
    const resumed = this.resumed;
    this.resumed = false;
    return resumed;
  }

  /** The card moved for real: this is a new placement that must earn its read. */
  private rearm(now: number): void {
    this.placementId = ++this.nextPlacementId;
    this.fired = false;
    this.firedStill = false;
    this.anchor = null;
    this.stillSince = null;
    this.trackedSince = now;
    this.tracked = 0;
    this.needsProof = false;
    this.retakes = 0;
    this.gaveUp = false;
    this.deferred = 0;
    this.deferUntil = 0;
  }

  /**
   * The loop has stopped looking. Treat it as exactly what it is: a gap.
   *
   * The gate used to learn about a gap only from a frame that arrived with no detection in
   * it, so every stretch the loop spent *not looking* was invisible to it — the live track
   * kept its last value, the latch with it, and the velocity window straddled the hole so
   * that a card exchanged inside one measured as the same card lying still. That window is
   * the riskiest moment in the whole loop, because the one thing that reliably precedes a
   * card being swapped is the previous card having just been named.
   *
   * Told rather than inferred from the sample timing, because "we chose not to look" and
   * "this device is slow" have identical signatures in a clock and opposite meanings: a
   * threshold on the interval between samples would deadlock the scanner on a phone whose
   * honest cadence sits above it. Idempotent — a long pause calls this on every poll and
   * only the first one has a track to drop.
   */
  blind(): void {
    if (this.centre) this.drop();
  }

  /**
   * The proof came back: the card in frame is not the one that was read.
   *
   * Exactly a re-arm, because that is what it is — a different card in the same spot is a
   * new placement, and it earns its read the ordinary way rather than being fired on the
   * strength of stillness the previous card accumulated.
   */
  swapped(): void {
    this.rearm(performance.now());
  }

  /**
   * Hand the read back unspent: the crop was not worth identifying yet.
   *
   * Not a retake — a retake is evidence that the card was read and refused, and this is
   * the opposite, a read that was never taken. It costs nothing from the budget, and it is
   * paced by its own short wait rather than by the read cooldown for the same reason.
   *
   * Returns whether the wait was granted. A placement that has already held out for its
   * whole budget is read as it is, because "wait for a better frame" has to end somewhere
   * and ending it with a rejection the user can see beats ending it with silence.
   */
  defer(waitMs: number, budgetMs: number): boolean {
    if (this.deferred >= budgetMs) return false;
    this.deferred += waitMs;
    this.deferUntil = performance.now() + waitMs;
    this.fired = false;
    return true;
  }

  /** A separate, single fresh-frame wait for sharpness selection; no focus budget spent. */
  sharpen(): void {
    this.deferUntil = performance.now() + 1;
    this.fired = false;
  }

  ownsPlacement(id: number): boolean {
    return this.centre ? this.placementId === id : this.placement?.id === id;
  }

  /** The proof came back the same card: the latch it was carrying stands. */
  confirmed(): void {
    this.needsProof = false;
  }

  /**
   * Give a rejected placement another chance, if it has any left.
   *
   * The card has not moved — stillness and anchor stay — but `fired` unlatches, so the
   * next still window past the cooldown reads again. Returns whether a retake was
   * granted, so the caller can say so.
   *
   * Reads run detached from the frame loop, so a verdict can land while the detection is
   * in a gap. The placement to re-arm is then the stashed one: applied to the empty live
   * track it would be overwritten by the resume, and the sleeve-gloss retry would be
   * lost in exactly the noisy conditions that make it worth having.
   */
  retake(): boolean {
    const dropped = this.centre ? null : this.placement;
    if (dropped) {
      if (dropped.retakes >= MAX_RETAKES) {
        this.gaveUp = true;
        return false;
      }
      dropped.retakes++;
      dropped.fired = false;
      return true;
    }
    if (!this.centre || this.retakes >= MAX_RETAKES) {
      this.gaveUp = true;
      return false;
    }
    this.retakes++;
    this.fired = false;
    return true;
  }

  /**
   * Whether the card is currently measured as resting — a full velocity window under
   * the still threshold, and every window since. The preview keys its tracking marker
   * on this: drawn on a moving card the marker just renders the detector's corner
   * jitter, which reads as broken rather than as tracking.
   */
  get still(): boolean {
    return this.stillSince != null;
  }

  /** Whether the placement in frame has already been read. Survives a healed dropout. */
  get latched(): boolean {
    return this.fired;
  }

  /**
   * Whether this placement ran out of attempts without ever being identified.
   *
   * A latch means "decided", and the two ways to be decided are not the same news: a card
   * that was read is one the log has, while a card that spent its retakes is one the
   * scanner has given up on and will not look at again until it moves. Reporting both as
   * "ready for the next card" is what makes a failure read as a success, and leaves the
   * user waiting on a scanner that has already stopped.
   */
  get exhausted(): boolean {
    return this.gaveUp;
  }

  update(detection: PoseDetection | null, allowFire = true): boolean {
    if (!detection) {
      this.drop();
      return false;
    }
    const now = performance.now();
    const quad = detection.quad;
    const centre = quadCentre(quad);
    const size = Math.sqrt(Math.max(detection.area, 1));

    this.lastSeenAt = now;

    if (this.centre) {
      const moved = Math.hypot(centre[0] - this.centre[0], centre[1] - this.centre[1]);
      // A jump this large is a different card, not the same one drifting.
      if (moved > REPLACED_FRACTION * size) {
        this.reset();
        // ...and a different card is not the placement that was dropped, either.
        this.placement = null;
      }
    } else {
      // No track: either the first sighting or the far side of a detection gap.
      this.resume(centre, size, now);
    }
    this.size = size;
    // A slow exchange never jumps in a single frame, but it does walk away from where
    // the read was taken.
    if (this.anchor) {
      const strayed = Math.hypot(centre[0] - this.anchor[0], centre[1] - this.anchor[1]);
      if (strayed > REPLACED_FRACTION * size) this.rearm(now);
    }
    this.centre = centre;
    this.tracked++;
    if (this.trackedSince == null) this.trackedSince = now;

    if (!this.baseline) {
      this.baseline = quad;
      this.baselineAt = now;
    } else if (now - this.baselineAt >= VELOCITY_WINDOW_MS) {
      const velocity = quadDrift(quad, this.baseline) / size / ((now - this.baselineAt) / 1000);
      this.lastVelocity = velocity;
      if (velocity > REARM_VELOCITY) {
        this.rearm(now);
      } else if (velocity > STILL_VELOCITY) {
        this.stillSince = null;
      } else if (this.stillSince == null) {
        // Still for the whole window, so stillness started when the window did.
        this.stillSince = this.baselineAt;
      }
      this.baseline = quad;
      this.baselineAt = now;
    }
    this.held = this.stillSince == null ? 0 : this.held + 1;

    const still = this.stillSince != null && now - this.stillSince >= STILL_MS;
    if (this.fired && (this.firedStill || !still)) return false;
    const timedOut = this.trackedSince != null && now - this.trackedSince >= MOTION_TIMEOUT_MS;
    if (!still && !timedOut) return false;
    // Keep tracking while recognition owns the only snapshot slot, but do not latch this
    // placement yet. The first frame after that read finishes can fire immediately.
    if (!allowFire) return false;
    // A placement waiting for a sharper crop is paced by that wait alone: no read was
    // spent, so the cooldown between reads has nothing to say about it.
    if (this.deferUntil) {
      if (now < this.deferUntil) return false;
    } else {
      // A placement that keeps rejecting backs off; a first read is paced by the cooldown
      // alone. Derived from the retake count and the last fire, so it survives a dropout
      // exactly as the budget it paces does — and so a *new* placement, whose retakes are
      // back at zero, never inherits the previous card's backoff.
      const backoff = this.retakes ? RETAKE_DELAYS[Math.min(this.retakes, MAX_RETAKES) - 1] : 0;
      if (now - this.lastFired < Math.max(READ_COOLDOWN_MS, backoff)) return false;
    }
    this.deferUntil = 0;
    this.lastFired = now;
    this.fired = true;
    this.firedStill = still;
    this.needsProof = false;
    this.gaveUp = false;
    this.anchor = centre;
    this.fireReason = still
      ? `still ${Math.round(now - (this.stillSince ?? now))}ms`
      : "motion timeout";
    return true;
  }
}
